/**
 * Novedades de cada versión de Rumbo, contadas por Gavi tras actualizar.
 *
 * Al publicar una versión (skill /rumbo-publicar) se añade su edición AL
 * PRINCIPIO de `EDICIONES`: 2-4 novedades, solo lo importante, en tono Gavi
 * y con lenguaje neutro (una versión grande puede contarlas todas, hasta 16:
 * la hoja se desliza). Si una versión no trae nada que contar a quien usa
 * la app, no se añade edición y la ventana no sale.
 */
import type { TFunction } from 'i18next';

export type Novedad = {
  /** Emoji del sistema dentro de su caja de color. */
  emoji: string;
  /** Hueco de color de la paleta de categorías (0..11, `--p-k*`). */
  hueco: number;
  titulo: string;
  texto: string;
};

export type Edicion = {
  /** Versión de Rumbo sin la «v» (la etiqueta de la release), p. ej. «0.8.0». */
  version: string;
  /** Lo que dice Gavi en su bocadillo. */
  saludo: string;
  novedades: Novedad[];
};

/** Ediciones, de la más nueva a la más vieja. Textos con `t()` (claves fijas). */
export function ediciones(t: TFunction): Edicion[] {
  return [
    {
      version: '0.12.7',
      saludo: t('Seagull on deck! The crew has a new screen:'),
      novedades: [
        {
          emoji: '⚓',
          hueco: 3,
          titulo: t('Your crew at a glance'),
          texto: t(
            'In "Share" each person is on one line, with "…" to change their permission or take them off the boat. Inviting is a step of its own.',
          ),
        },
        {
          emoji: '✉️',
          hueco: 5,
          titulo: t('An invitation that explains itself'),
          texto: t(
            'The link travels with a message that says who invites and to which boat. On the computer you can copy it or send it by email.',
          ),
        },
      ],
    },
    {
      version: '0.12.5',
      saludo: t('Seagull on deck! Four things you will notice:'),
      novedades: [
        {
          emoji: '🧮',
          hueco: 4,
          titulo: t('I do the sums'),
          texto: t(
            'Ask me for your net worth or when you will finish paying your debts: I give you the number and show you the calculation.',
          ),
        },
        {
          emoji: '🗣️',
          hueco: 0,
          titulo: t('In your language, and in full'),
          texto: t(
            'I answer in the language you ask in, and my answers no longer get cut off halfway.',
          ),
        },
        {
          emoji: '🔑',
          hueco: 7,
          titulo: t('Only with Google'),
          texto: t(
            'To sign in you now use Google, and nothing else. The email way is gone.',
          ),
        },
        {
          emoji: '👀',
          hueco: 8,
          titulo: t('I can see your numbers'),
          texto: t(
            'Ask me what you have spent this month and I look at your budget to answer. Before I start I tell you what travels, and you decide.',
          ),
        },
      ],
    },
    {
      version: '0.12.3',
      saludo: t('Seagull on deck! Two fixes so your accounts add up:'),
      novedades: [
        {
          emoji: '🔁',
          hueco: 3,
          titulo: t('Transfers that take nothing from the bank'),
          texto: t(
            'If you delete or change a transfer, the transaction the bank brought stays in its account, with its category.',
          ),
        },
        {
          emoji: '🏦',
          hueco: 9,
          titulo: t('The bank, on its way'),
          texto: t(
            'In More and in the menu of each account you will see «Connect to the bank · Coming soon». It is not for everyone yet; I will let you know when it arrives.',
          ),
        },
      ],
    },
    {
      version: '0.12.2',
      saludo: t('Seagull on deck! Three fixes you will actually notice:'),
      novedades: [
        {
          emoji: '🔁',
          hueco: 3,
          titulo: t('Transfers without duplicates'),
          texto: t(
            'If the bank already brought the money into the other account, I link the transfer instead of writing it down twice.',
          ),
        },
        {
          emoji: '☁️',
          hueco: 9,
          titulo: t('The cloud, without scares'),
          texto: t(
            'If there is a newer version of your budget in the cloud and nothing here is waiting to be uploaded, I bring it myself, with no red warning. And «View only» no longer shows the warning when you open it.',
          ),
        },
        {
          emoji: '🐦',
          hueco: 4,
          titulo: t('My advice, one tap away'),
          texto: t(
            'Tap the advice card in Home and it opens in full. No need to look for «See all» anymore.',
          ),
        },
      ],
    },
    {
      version: '0.12.1',
      saludo: t('Seagull on deck! Little cargo, but the good kind:'),
      novedades: [
        {
          emoji: '👆',
          hueco: 3,
          titulo: t('Hold it down in the Plan'),
          texto: t(
            'Hold down a category or a group and it opens to change its name, icon or group. A short tap still opens the keypad.',
          ),
        },
        {
          emoji: '📸',
          hueco: 11,
          titulo: t('Report it from the same screen'),
          texto: t(
            'In the compass of every screen, «Report a problem here» sends a photo with your figures covered and whatever you write.',
          ),
        },
        {
          emoji: '💶',
          hueco: 4,
          titulo: t('Extra pay and covered months'),
          texto: t(
            'An extra pay counts as arrived when the money actually comes in, and «Covered months» no longer gets mixed up by what you move between categories.',
          ),
        },
        {
          emoji: '⚖️',
          hueco: 7,
          titulo: t('Your first budget adds up'),
          texto: t(
            'If you start with a statement, «To Budget» matches your balance from the first moment, and the adjustment to match the bank no longer asks for a category.',
          ),
        },
        {
          emoji: '☁️',
          hueco: 9,
          titulo: t('Cloud warnings, in plain words'),
          texto: t(
            'If your budget changed in the cloud, I tell you what is going on and what to do, without asking for passwords nobody knows.',
          ),
        },
        {
          emoji: '⛵',
          hueco: 6,
          titulo: t('While it loads'),
          texto: t(
            'When something takes a while, the sailboat comes out and I tell you something to make the wait shorter.',
          ),
        },
        {
          emoji: '📝',
          hueco: 10,
          titulo: t('Tell me how it is going'),
          texto: t(
            'After a few days I ask you what you think, one question per screen. It is also in Settings, next to «Something is wrong».',
          ),
        },
      ],
    },
    {
      version: '0.12.0',
      saludo: t('Seagull on deck! This time I bring lots of things:'),
      novedades: [
        {
          emoji: '📖',
          hueco: 0,
          titulo: t('I explain it to you'),
          texto: t(
            'On every screen, tap the porthole at the top and I show you what each thing is for, step by step.',
          ),
        },
        {
          emoji: '🐦',
          hueco: 4,
          titulo: t("Gavi's advice"),
          texto: t(
            'The tips in Home, Reports and each category now come from me, in my own voice. The numbers stay the same.',
          ),
        },
        {
          emoji: '🧑‍✈️',
          hueco: 1,
          titulo: t('Crew'),
          texto: t(
            'From the «Aboard» card you decide what each person can touch, remove someone or get off the boat yourself.',
          ),
        },
        {
          emoji: '🪙',
          hueco: 4,
          titulo: t('Moving money, easier'),
          texto: t(
            'When you pick where from and where to, categories come in groups, with a search box and the most likely ones first.',
          ),
        },
        {
          emoji: '↩️',
          hueco: 2,
          titulo: t('Back, for real'),
          texto: t(
            "Your phone's back button now takes you… actually back, not who knows where. It remembers where you were and, if you were about to lose something you typed, I warn you first.",
          ),
        },
        {
          emoji: '🧩',
          hueco: 3,
          titulo: t('Home and Reports, your way'),
          texto: t(
            "Move the cards, change their size, remove the ones you don't use and add others.",
          ),
        },
        {
          emoji: '🍾',
          hueco: 5,
          titulo: t('Savings, in their place'),
          texto: t(
            "In the Plan compass, «Savings» gathers all your voyages: what you have, the date and this month's share.",
          ),
        },
        {
          emoji: '🔍',
          hueco: 6,
          titulo: t('Review, all together'),
          texto: t(
            'After importing a statement, «Review» takes you to everything new, with or without a category.',
          ),
        },
        {
          emoji: '🤔',
          hueco: 8,
          titulo: t('Did you already have it?'),
          texto: t(
            'If something in the statement looks like what you jotted down by hand, I ask whether it is the same instead of noting it twice.',
          ),
        },
        {
          emoji: '🏦',
          hueco: 9,
          titulo: t('Clearer debts'),
          texto: t(
            "In a debt, «Pay an installment» from any account you like, and the month's interest is noted on its own.",
          ),
        },
        {
          emoji: '🔢',
          hueco: 10,
          titulo: t("Always Rumbo's keypad"),
          texto: t(
            "Every amount is typed with Rumbo's keypad, also the ones that used to open the phone's.",
          ),
        },
        {
          emoji: '✉️',
          hueco: 11,
          titulo: t('Something is wrong / Ask for a feature'),
          texto: t(
            'In Settings › Your data you can write to us if something fails or if you miss something.',
          ),
        },
        {
          emoji: '📱',
          hueco: 0,
          titulo: t('Better on iPhone'),
          texto: t(
            'Windows no longer hide behind the keyboard, and the Plan keypad closes by sliding it down.',
          ),
        },
        {
          emoji: '🔧',
          hueco: 2,
          titulo: t('And fixes'),
          texto: t(
            "Each category's goals add up right, «Delete» asks first and «Hide» can be undone.",
          ),
        },
      ],
    },
    {
      version: '0.11.0',
      saludo: t(
        'Seagull on deck! I bring a full hold and a bottle with a letter:',
      ),
      novedades: [
        {
          emoji: '🧭',
          hueco: 7,
          titulo: t('Today, in two taps'),
          texto: t(
            'See how much you can spend today and jot down an expense in two taps.',
          ),
        },
        {
          emoji: '🍾',
          hueco: 4,
          titulo: t('The bottle and the logbook'),
          texto: t(
            'Your savings are voyages: seal a letter, sail and open it on arrival.',
          ),
        },
        {
          emoji: '📥',
          hueco: 2,
          titulo: t('Your bank statement on board'),
          texto: t(
            'Bring in the one from your bank, history included, and I ask about each shop once.',
          ),
        },
        {
          emoji: '📓',
          hueco: 0,
          titulo: t('Logbook of the week'),
          texto: t(
            'Every Monday and on the 1st I tell you how it went. Turn on notifications in Settings.',
          ),
        },
      ],
    },
    {
      version: '0.10.0',
      saludo: t(
        'Seagull on deck! This time I bring a padlock and a dictionary:',
      ),
      novedades: [
        {
          emoji: '🗝️',
          hueco: 0,
          titulo: t('Your chest with a key'),
          texto: t(
            'Create your Rumbo password and keep the spare key: your accounts travel locked.',
          ),
        },
        {
          emoji: '🗣️',
          hueco: 4,
          titulo: t('Now also in Catalan'),
          texto: t('In Settings → Language, Castellano or Català.'),
        },
        {
          emoji: '↩️',
          hueco: 2,
          titulo: t('Full astern'),
          texto: t(
            'In the first voyage you can go back a step, and I talk to you the way you prefer.',
          ),
        },
      ],
    },
    {
      version: '0.9.0',
      saludo: t('Seagull on deck! I bring fresh news from the harbour:'),
      novedades: [
        {
          emoji: '🚢',
          hueco: 7,
          titulo: t('A whole fleet'),
          texto: t(
            'Now you can have more than one budget: launch another boat from the harbour.',
          ),
        },
        {
          emoji: '🧭',
          hueco: 2,
          titulo: t('The first voyage, again'),
          texto: t('In Settings you can sail with me again whenever you want.'),
        },
        {
          emoji: '🐦',
          hueco: 4,
          titulo: t('Your choice of crew'),
          texto: t(
            'When you launch a boat you choose: with me or on your own.',
          ),
        },
        {
          emoji: '⚓',
          hueco: 9,
          titulo: t('Clearer changes of owner'),
          texto: t('If "Move to my account" fails, now I tell you why.'),
        },
      ],
    },
    {
      version: '0.8.0',
      saludo: t(
        'Ahoy there! While you were away I have been mending the sails. Take a look:',
      ),
      novedades: [
        {
          emoji: '🔒',
          hueco: 0,
          titulo: t('More locks on the hold'),
          texto: t('Your account and your banks, better protected.'),
        },
        {
          emoji: '🙋',
          hueco: 7,
          titulo: t('I ask you first'),
          texto: t('I ask before sending usage data. You decide.'),
        },
        {
          emoji: '👀',
          hueco: 8,
          titulo: t("Look but don't touch"),
          texto: t('With "View only", nobody touches your bank.'),
        },
      ],
    },
  ];
}

/** Edición a enseñar: la más nueva, si en este dispositivo aún no se vio. */
export function edicionPendiente(
  lista: Edicion[],
  vista: string | null,
): Edicion | null {
  const ultima = lista[0];
  if (!ultima || ultima.novedades.length === 0 || ultima.version === vista) {
    return null;
  }
  return ultima;
}

const CLAVE_VISTA = 'rumbo:novedades:vista';

/** Última versión cuyas novedades se vieron (o se saltaron) aquí. */
export function versionVista(): string | null {
  try {
    return window.localStorage.getItem(CLAVE_VISTA);
  } catch {
    return null;
  }
}

export function marcarVista(version: string) {
  try {
    window.localStorage.setItem(CLAVE_VISTA, version);
  } catch {
    // Sin almacenamiento (modo privado): volverá a salir, sin más.
  }
}
